import axiosService from '../utils/axios';
import { Notification, HandleNotificationRequest } from '@/types/notification';

// Fetch notifications for a user
export const getNotifications = async (page: number, limit: number, includeDismissed: boolean = false): Promise<Notification[]> => {
    const response = await axiosService.get('/notifications', {
        params: { page, limit, includeDismissed }
    });

    return response.data.notifications;
};

export const handleNotification = async (
    notificationId: string,
    data: HandleNotificationRequest
): Promise<void> => {
    await axiosService.put(`/notifications/${notificationId}`, data);
};

export const markNotificationsAsRead = async (): Promise<void> => {
    await axiosService.post('/notifications/mark-as-read');
};

export const clearAllNotifications = async (): Promise<void> => {
    await axiosService.post('/notifications/clear-all');
};

// Get only the latest notification for the header display
export const getLatestNotification = async (): Promise<Notification | null> => {
    const response = await axiosService.get('/notifications/latest');
    return response.data.notification || null;
};

export const getUnreadCount = async (): Promise<number> => {
    const response = await axiosService.get('/notifications/unread-count');
    return response.data.unreadCount || 0;
};

// Function to dismiss all collab notifications from a specific user
export const dismissCollabNotificationsFromUser = async (fromUserId: string): Promise<void> => {
    try {
        // Get all notifications
        const allNotifications = await getNotifications(1, 100); // Get a large batch

        // Filter to find pending collab notifications from the specific user
        const notificationsToHandle = allNotifications.filter(n =>
            n.type === 'C' &&
            n.fromUserId === fromUserId &&
            n.isActionable &&
            n.action === 'accept'
        );

        // Dismiss each notification
        const dismissPromises = notificationsToHandle.map(notification =>
            handleNotification(notification._id, { status: 'D' })
        );

        await Promise.all(dismissPromises);
    } catch (error) {
        console.error('Error dismissing collab notifications:', error);
    }
};

// Global notification refresh function that can be shared
let notificationRefreshCallback: (() => void) | null = null;
let forceRefreshCallback: (() => void) | null = null;

export const setNotificationRefreshCallback = (callback: () => void, forceCallback?: () => void) => {
    notificationRefreshCallback = callback;
    if (forceCallback) {
        forceRefreshCallback = forceCallback;
    }
};

// Track whether refresh is in progress to prevent infinite loops
let isRefreshing = false;

export const refreshNotifications = () => {
    // Prevent multiple overlapping refreshes
    if (isRefreshing) return;

    isRefreshing = true;

    // First, try the force refresh which always fetches regardless of panel state
    if (forceRefreshCallback) {
        // Immediate call for critical updates
        forceRefreshCallback();
    }

    // Also queue the regular refresh with a shorter delay
    if (notificationRefreshCallback) {
        // Reduced to 500ms for faster updates but still allowing backend time to process
        setTimeout(() => {
            notificationRefreshCallback?.();
            // Reset the refresh flag after a delay to allow future refreshes
            setTimeout(() => {
                isRefreshing = false;
            }, 1000);
        }, 500);
    } else {
        // Reset immediately if no callback is set
        isRefreshing = false;
    }
};

export const dismissNotification = async (notificationId: string): Promise<void> => {
    await handleNotification(notificationId, { status: 'D' });
};

export const markAsRead = async (notificationId: string): Promise<void> => {
    await handleNotification(notificationId, { status: 'R' });
};
